// Real auth: proxies WP JWT through our FastAPI backend.
// Stores the JWT + user record in localStorage. On app boot, the stored token
// is verified server-side via /api/auth/me; if invalid we clear the session.
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { safeReadJson, API } from "@/lib/api";

const KEY = "geovinum.auth.v1";

const AuthContext = createContext(null);

function readStored() {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function writeStored(value) {
  if (value) localStorage.setItem(KEY, JSON.stringify(value));
  else localStorage.removeItem(KEY);
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null); // { token, user }
  const [ready, setReady] = useState(false);

  // Re-validate stored token on mount
  useEffect(() => {
    const stored = readStored();
    if (!stored?.token) { setReady(true); return; }
    let cancelled = false;
    fetch(`${API}/api/auth/me`, {
      headers: { Authorization: `Bearer ${stored.token}` },
    })
      .then(async (r) => (r.ok ? safeReadJson(r) : Promise.reject(r)))
      .then((user) => { if (!cancelled) { const next = { token: stored.token, user }; setSession(next); writeStored(next); } })
      .catch(() => { if (!cancelled) { writeStored(null); setSession(null); } })
      .finally(() => { if (!cancelled) setReady(true); });
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async ({ email, password }) => {
    let r;
    try {
      r = await fetch(`${API}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
    } catch (e) {
      throw new Error("Network error — check your connection");
    }
    const payload = await safeReadJson(r);
    if (payload?._empty || payload?._raw) {
      // Backend gave non-JSON (HTML SPA fallback, Cloudflare error page,
      // etc.). Surface a helpful diagnostic instead of crashing the form.
      throw new Error(`Server returned an unexpected response (HTTP ${payload.status || r.status}).`);
    }
    if (!payload?.ok) {
      throw new Error(payload?.detail || "Login failed");
    }
    const next = { token: payload.token, user: payload.user };
    setSession(next);
    writeStored(next);
    // Persist the current site lang so transactional emails ship in their lang.
    try {
      const lang = (new URLSearchParams(window.location.search).get("lang") || localStorage.getItem("geovinum.lang") || "fr").slice(0, 2);
      fetch(`${API}/api/profile/locale`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${payload.token}` },
        body: JSON.stringify({ locale: lang }),
      }).catch(() => {});
    } catch (_e) { /* silent */ }
    return payload.user;
  }, []);

  // Real signup: backend creates a WP user via admin app password, then returns
  // a JWT exactly like /login. Frontend then transitions straight to dashboard.
  const signup = useCallback(async ({ email, password, name }) => {
    let r;
    try {
      r = await fetch(`${API}/api/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, name }),
      });
    } catch (e) {
      throw new Error("Network error — check your connection");
    }
    const payload = await safeReadJson(r);
    if (payload?._empty || payload?._raw) {
      throw new Error(`Server returned an unexpected response (HTTP ${payload.status || r.status}).`);
    }
    if (!payload?.ok) {
      throw new Error(payload?.detail || "Registration failed");
    }
    const next = { token: payload.token, user: payload.user };
    setSession(next);
    writeStored(next);
    return payload.user;
  }, []);

  const forgotPassword = useCallback(async (email) => {
    try {
      await fetch(`${API}/api/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
    } catch { /* fire-and-forget */ }
    return { ok: true };
  }, []);

  const logout = useCallback(() => { writeStored(null); setSession(null); }, []);

  const user = session?.user || null;
  const token = session?.token || null;

  return (
    <AuthContext.Provider value={{ user, token, ready, login, signup, logout, forgotPassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
